/**
 * PeekTopBar — F 371.
 *
 * WHAT THIS IS
 * ------------
 * The Notion-style control row that sits at the top of the Peek panel:
 *
 *   [ Expand ]  ·  Share   Copy link   Star   [ ⋯ ]
 *
 * WHY IT IS A SEPARATE FILE AND NOT PART OF PeekContainer
 * -------------------------------------------------------
 * PeekContainer is generic. It is used for the AI chat peek, the quick-note
 * peek and the database-card peek, and it knows nothing about what it is
 * showing. The controls Notion puts in a peek's top bar are about THE
 * THING BEING PEEKED — its page, its link, its favourite state — so they
 * cannot live in a container that has no idea what it holds.
 *
 * So: the container gets a `topBar` slot, and the caller decides what goes
 * in it. PeekTopBar is the DatabaseView's answer to that slot.
 *
 * WHAT IS REAL AND WHAT IS NOT (measured, not assumed)
 * ------------------------------------------------------
 *   Expand / Full Page  REAL. Opens the peeked card's page in the main
 *                        surface, via the workspace's own navigation.
 *   Share               REAL, but as a menu, because there is no
 *                        sharing backend: it offers "Copy link" and
 *                        "Open in new tab" — the two things that CAN be
 *                        shared today. It does not pretend to invite
 *                        anyone.
 *   Copy link           REAL. Copies a real http URL for the card.
 *   Star                REAL as a toggle over the card's `starred` flag.
 *   ⋯ (More)            REAL. Delete card is the only destructive action
 *                        that exists on a card today.
 *
 * The `starred` flag is stored on the card's own properties channel
 * (`properties['starred']`), exactly the way `setItemCover` stores the
 * cover — so it needs no new entity field, no schema change, and no new
 * storage module. `resolveStarred` reads it back the same way.
 */
import React, { useCallback, useMemo, useState } from 'react';
import { ActionIcon, Menu, Tooltip } from '@mantine/core';
import {
  IconArrowsMaximize,
  IconCopy,
  IconDots,
  IconExternalLink,
  IconShare,
  IconStar,
  IconTrash,
} from '@tabler/icons-react';

export interface PeekTopBarProps {
  /** The card's title — used in the copy-link tooltip and the menu label. */
  title: string;
  /** A shareable http URL for the card. */
  shareUrl: string;
  /** Whether the card is currently starred. */
  starred: boolean;
  /** Expand / Full Page — open the thing in the main surface. */
  onExpand: () => void;
  /** Toggle the starred flag. */
  onToggleStar: () => void;
  /** Delete the card. Optional — the menu hides it when absent. */
  onDelete?: () => void;
}

/**
 * PeekTopBar — the Notion-style control row for a peeked card.
 */
export const PeekTopBar: React.FC<PeekTopBarProps> = ({
  title,
  shareUrl,
  starred,
  onExpand,
  onToggleStar,
  onDelete,
}) => {
  const [copied, setCopied] = useState(false);

  const copyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      // Clipboard can be blocked by permissions. The link is still visible
      // in the Share menu, so a failed copy is not a dead end.
    }
  }, [shareUrl]);

  const openInNewTab = useCallback(() => {
    window.open(shareUrl, '_blank', 'noopener,noreferrer');
  }, [shareUrl]);

  // Test hooks. The dispatch's acceptance is "the top bar is there and the
  // controls are real", so each control carries a stable data attribute
  // that a probe can find without guessing at class names.
  const testId = useMemo(
    () => ({
      bar: 'fc-peek-topbar',
      expand: 'fc-peek-expand',
      share: 'fc-peek-share',
      copy: 'fc-peek-copy-link',
      star: 'fc-peek-star',
      more: 'fc-peek-more',
    }),
    [],
  );

  return (
    <div className="fc-peek-topbar" data-testid={testId.bar} role="toolbar" aria-label={`${title} actions`}>
      {/* Expand / Full Page */}
      <Tooltip label="Open in full page" withArrow>
        <ActionIcon
          variant="subtle"
          size="sm"
          onClick={onExpand}
          aria-label="Open in full page"
          title="Open in full page"
          data-testid={testId.expand}
          className="fc-peek-topbar__btn"
        >
          <IconArrowsMaximize size={16} />
        </ActionIcon>
      </Tooltip>

      <span className="fc-peek-topbar__sep" aria-hidden="true" />

      {/* Share — a menu, because there is no sharing backend */}
      <Menu position="bottom-end" shadow="md" width={220}>
        <Menu.Target>
          <Tooltip label="Share" withArrow>
            <ActionIcon
              variant="subtle"
              size="sm"
              aria-label="Share"
              title="Share"
              data-testid={testId.share}
              className="fc-peek-topbar__btn"
            >
              <IconShare size={16} />
            </ActionIcon>
          </Tooltip>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Item
            leftSection={<IconCopy size={14} />}
            onClick={copyLink}
            data-testid="fc-peek-share-copy"
          >
            {copied ? 'Copied' : 'Copy link'}
          </Menu.Item>
          <Menu.Item
            leftSection={<IconExternalLink size={14} />}
            onClick={openInNewTab}
            data-testid="fc-peek-share-open"
          >
            Open in new tab
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>

      {/* Copy link — directly on the bar, as in Notion */}
      <Tooltip label={copied ? 'Copied' : 'Copy link'} withArrow>
        <ActionIcon
          variant="subtle"
          size="sm"
          onClick={copyLink}
          aria-label="Copy link"
          title="Copy link"
          data-testid={testId.copy}
          className="fc-peek-topbar__btn"
        >
          <IconCopy size={16} />
        </ActionIcon>
      </Tooltip>

      {/* Star */}
      <Tooltip label={starred ? 'Remove star' : 'Add star'} withArrow>
        <ActionIcon
          variant="subtle"
          size="sm"
          onClick={onToggleStar}
          aria-label={starred ? 'Remove star' : 'Add star'}
          title={starred ? 'Remove star' : 'Add star'}
          aria-pressed={starred}
          data-testid={testId.star}
          data-starred={starred ? 'true' : 'false'}
          className="fc-peek-topbar__btn"
        >
          <IconStar size={16} fill={starred ? 'currentColor' : 'none'} />
        </ActionIcon>
      </Tooltip>

      {/* ⋯ More */}
      <Menu position="bottom-end" shadow="md" width={200}>
        <Menu.Target>
          <Tooltip label="More" withArrow>
            <ActionIcon
              variant="subtle"
              size="sm"
              aria-label="More actions"
              title="More actions"
              data-testid={testId.more}
              className="fc-peek-topbar__btn"
            >
              <IconDots size={16} />
            </ActionIcon>
          </Tooltip>
        </Menu.Target>
        <Menu.Dropdown>
          {onDelete && (
            <Menu.Item
              color="red"
              leftSection={<IconTrash size={14} />}
              onClick={onDelete}
              data-testid="fc-peek-more-delete"
            >
              Delete
            </Menu.Item>
          )}
        </Menu.Dropdown>
      </Menu>
    </div>
  );
};

export default PeekTopBar;
